// components
import { Stack } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import { GridCompact12To4ExtraLarge } from 'components/shared/Grid/Grid.components';
import { StyledTypographyHtmlFontSize } from 'components/shared/Typography/Typography.styles';
import {
  CustomSortableTaskPaper,
  StyledCountTypography,
  StyledCustomMarginIconButton,
  StyledSortableTaskIconButton,
  StyledSortableTaskIconButtonGrabbingCursor,
  StyledSortableTaskStack
} from './SortableTask.styles';
import { StyledTitleTypography } from './TasksList.styles';

// constants
import { iconsMapping } from './TasksList.const';

// hooks
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';

// types
import { SortableTaskProps } from './SortableTask.types';

// utils
import { CSS } from '@dnd-kit/utilities';
import {
  StyledRowStackAlignItemsAltGap,
  StyledRowStackSmallGap,
  StyledRowStackStartSpaceBetweenSmallGap,
  StyledStackSpaceBetweenFullHeight
} from 'components/shared/Stack/Stack.styles';
import dayjs from 'dayjs';

export const SortableTask = ({
  dataKey,
  onRemoveTile,
  items,
  onTaskClicked
}: SortableTaskProps) => {
  const { t, i18n } = useTranslation('common');
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: dataKey });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 9999 : 'auto'
  };

  const finishedAllItemTasks = items.every((item) => item.count === 0);

  return (
    <GridCompact12To4ExtraLarge key={dataKey} id={dataKey}>
      <CustomSortableTaskPaper
        key={dataKey}
        ref={setNodeRef}
        style={style}
        fixedHeight={220}
        finishedAllItemTasks={finishedAllItemTasks}
      >
        <StyledStackSpaceBetweenFullHeight>
          <StyledRowStackStartSpaceBetweenSmallGap>
            <StyledRowStackSmallGap>
              <Icon
                type={iconsMapping[dataKey]}
                className="group-icon"
                fontSize="medium"
              />
              <StyledTitleTypography variant="h6" lang={i18n.language}>
                {t('taskDefinitionsShort')[dataKey]}
              </StyledTitleTypography>
            </StyledRowStackSmallGap>

            <StyledRowStackAlignItemsAltGap>
              <StyledSortableTaskIconButtonGrabbingCursor
                className="tile-remove"
                type="grip-dots-vertical"
                color="inherit"
                {...attributes}
                {...listeners}
              />
              <StyledSortableTaskIconButton
                className="tile-remove"
                type="xmark"
                color="inherit"
                onClick={() => onRemoveTile(dataKey)}
              />
            </StyledRowStackAlignItemsAltGap>
          </StyledRowStackStartSpaceBetweenSmallGap>
          <Stack>
            {items.map(({ definitionKey, count, dynamicLabel, date }) => (
              <StyledSortableTaskStack
                key={definitionKey}
                onClick={() => onTaskClicked(definitionKey)}
              >
                <StyledTypographyHtmlFontSize variant="body1">
                  {dynamicLabel
                    ? t(`taskDefinitionsShort.${dynamicLabel}`, {
                        date: dayjs(date).format('MMM YYYY')
                      })
                    : t('taskDefinitionsShort')[definitionKey]}
                </StyledTypographyHtmlFontSize>

                {count > 0 ? (
                  <StyledCountTypography count={count}>
                    {count}
                  </StyledCountTypography>
                ) : (
                  <StyledCustomMarginIconButton type="check" color="success" />
                )}
              </StyledSortableTaskStack>
            ))}
          </Stack>
        </StyledStackSpaceBetweenFullHeight>
      </CustomSortableTaskPaper>
    </GridCompact12To4ExtraLarge>
  );
};
